import type { FilterOption } from "components/shared/FiltersAutocomplete";
import type { IChannel } from "containers/Channels/types";
import type { ITitle } from "containers/Titles/types";
import { getCountries } from "countries";
import { formatNumber, getCleanStartorEndDay } from "helpers/utils";
import { t } from "i18next";
import { useMemo } from "react";
import type { AnalyticsChartType, DashboardData } from "store/analyticsStore";
import type { IDatesRange } from "types";

import type { DataType } from "./SalesComparisonChart/utils";
import type { ChosenOptions } from "./SalesComparisonChart/utils";
import type { TopDataType } from "./TopPerformingDataBlock";

/** Max end date for Analytics date pickers when user is free to choose any range (so 2026+ is visible). */
export const ANALYTICS_DATE_PICKER_MAX_END = new Date(2030, 11, 31);

/**
 * Format Data for block card
 * @param dashboard dashboard data
 * @param isFirstPeriod check if it's the first period
 */
export const blockCardsDataTransform = (dashboard: DashboardData | undefined, isFirstPeriod: boolean) => {
    if (!dashboard) return [];

    const getEvolution = (type: string) => {
        if (isFirstPeriod) return "-";
        const N = dashboard.metrics?.[type].N ?? 0;
        const N_1 = dashboard.metrics?.[type].N_1 ?? 0;

        return +(N / N_1).toFixed(2);
    };

    return [
        [
            {
                unit: dashboard.metrics?.nbTitles.N,
                label: t("Analytics.NbPublishedTitles"),
                evolution: getEvolution("nbTitles"),
                evolutionLabel: "%",
            },
        ],
        [
            {
                unit: formatNumber(dashboard.metrics?.totalSales.N, {
                    isCurrency: true,
                }),
                label: t("Analytics.TotalIncomeTitles"),
                evolution: getEvolution("totalSales"),
                evolutionLabel: "%",
            },
        ],
        [
            {
                unit: formatNumber(dashboard.metrics?.totalRoyalties.N, {
                    isCurrency: true,
                }),
                label: t("Analytics.TotalRoyalties"),
                evolution: getEvolution("totalRoyalties"),
                evolutionLabel: "%",
            },
        ],
    ];
};

export type AnalyticsLayout = {
    component?: JSX.Element;
    layout: {
        i: string;
        x: number;
        y: number;
        w: number;
        h: number;
        minH: number;
        minW: number;
    };
    /**
     * Loading state for custom style on graph block
     */
    isLoading?: boolean;
};

export type AnalyticsOptions = {
    titles: FilterOption[];
    channels: FilterOption[];
    countryMarkets: FilterOption[];
};

export type StoredPreferences = {
    storedLayout: AnalyticsLayout[];
    storedFilters: Record<AnalyticsChartType, ChosenOptions>;
};

/**
 * Format filters options
 * @param titles Titles data
 * @param channels Channels data
 * @param analyticsCountries Countries with found data
 */
export const useFilterOptions = (titles: ITitle[], channels: IChannel[], analyticsCountries: string[]) => {
    return useMemo(() => {
        const formatOption = (elements: any) =>
            elements.map((item) => ({ id: item?.id || item.code, name: item.name }));

        const filteredCountries = getCountries().filter((country) => analyticsCountries.includes(country.code));

        return {
            titles: formatOption(titles),
            channels: formatOption(channels),
            countryMarkets: formatOption(filteredCountries),
        } as AnalyticsOptions;
    }, [titles, channels, analyticsCountries]);
};

/**
 * Format preferences settings (filters/layout) to save to company settings
 * @param preference updated layout after moving/resizing block or last applied filters
 * @param storedPreferences stored local storage preferences about filters/layout
 */
export const settingsTransform = (
    type: "layout" | "filters",
    preferences:
        | AnalyticsLayout[]
        | ChosenOptions
        | {
              chosenDates: IDatesRange;
              dataType?: DataType | TopDataType;
          },
    storedPreferences: any,
    chartType?: AnalyticsChartType,
) => {
    let storedLayout = {};
    let storedFilters = {};

    if (type === "layout") {
        storedLayout = (preferences as AnalyticsLayout[]).map((item) => item.layout);
        storedFilters = storedPreferences?.storedFilters ?? {};
    }
    if (type === "filters") {
        storedLayout = storedPreferences?.storedLayout ?? {};
        storedFilters = { ...storedPreferences?.storedFilters, [chartType as string]: preferences };
    }

    return JSON.stringify({ storedLayout, storedFilters });
};

/**
 * Get initial layout state, chose stored if one is stored into local storage
 * or pick default one
 */
export const getStoredLayoutOrDefault = (storedPreferences, defaultLayout) => {
    const storedLayout = storedPreferences?.storedLayout;
    if (storedLayout) {
        return defaultLayout.map((item, index) => ({
            ...item,
            layout: storedLayout[index] || item.layout,
        }));
    } else {
        return defaultLayout;
    }
};

/**
 * Hydrate dates to start and end of day
 * Avoid timezone issues
 * @param dates dates to hydrate
 * @returns hydrated dates
 */
export const hydrateDates = (dates: IDatesRange) => {
    try {
        return {
            startDate: getCleanStartorEndDay(new Date(dates.startDate)),
            endDate: getCleanStartorEndDay(new Date(dates.endDate), { time: "end" }),
        };
    } catch (err) {
        return dates;
    }
};
